import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

export type AvatarNotificationBadgeVariant =
  | "warning"
  | "success"
  | "error"
  | "info"
  | "neutral";

export type AvatarNotificationBadgePosition =
  | "top-right"
  | "top-left"
  | "bottom-right"
  | "bottom-left";

export type AvatarNotificationBadgeSize = "xs" | "sm" | "lg";

export type AvatarNotificationBadgeProps = {
  /** Color variant. @default "warning" */
  variant?: AvatarNotificationBadgeVariant;
  /** Dot size — matches avatar size. @default "sm" */
  size?: AvatarNotificationBadgeSize;
  /** Corner to anchor the dot. @default "top-right" */
  position?: AvatarNotificationBadgePosition;
  /** Accessible label for meaningful status badges. Ignored when decorative. */
  ariaLabel?: string;
  /** Decorative badges should not be announced by assistive technology. @default true */
  decorative?: boolean;
  className?: string;
};

// ─── Variants ─────────────────────────────────────────────────────────────────

export const avatarBadgeVariants = cva(
  "absolute rounded-full ring-background",
  {
    variants: {
      variant: {
        warning: "bg-warning",
        success: "bg-success",
        error: "bg-destructive",
        info: "bg-primary",
        neutral: "bg-muted-foreground",
      },
      size: {
        xs: "size-1.5 ring-1",
        sm: "size-2 ring-1",
        lg: "size-2.5 ring-2",
      },
      position: {
        "top-right": "top-0 right-0",
        "top-left": "top-0 left-0",
        "bottom-right": "right-0 bottom-0",
        "bottom-left": "bottom-0 left-0",
      },
    },
    compoundVariants: [
      // xs: flush with the container edge — no outward peek
      // sm: 4px peek so the dot reads clearly against the icon
      { size: "sm", position: "top-right", className: "-top-0.5 -right-1" },
      { size: "sm", position: "top-left", className: "-top-0.5 -left-1" },
      {
        size: "sm",
        position: "bottom-right",
        className: "-right-1 -bottom-0.5",
      },
      { size: "sm", position: "bottom-left", className: "-bottom-0.5 -left-1" },
      { size: "lg", position: "top-right", className: "-top-0.5 -right-0.5" },
      { size: "lg", position: "top-left", className: "-top-0.5 -left-0.5" },
      {
        size: "lg",
        position: "bottom-right",
        className: "-right-0.5 -bottom-0.5",
      },
      {
        size: "lg",
        position: "bottom-left",
        className: "-bottom-0.5 -left-0.5",
      },
    ],
    defaultVariants: {
      variant: "warning",
      size: "sm",
      position: "top-right",
    },
  }
);

const variantLabel: Record<AvatarNotificationBadgeVariant, string> = {
  warning: "Warning notification",
  success: "Success notification",
  error: "Error notification",
  info: "Info notification",
  neutral: "Notification",
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Status/notification dot positioned over an avatar.
 * Sized and colored via variants. Rendering is unconditional — the caller controls visibility.
 *
 * @example
 * ```tsx
 * // Show only when there are unread notifications
 * {count > 0 && <AvatarNotificationBadge size="lg" variant="warning" />}
 * ```
 */
export function AvatarNotificationBadge({
  variant = "warning",
  size,
  position,
  ariaLabel,
  decorative = true,
  className,
}: AvatarNotificationBadgeProps) {
  const label = ariaLabel ?? variantLabel[variant];

  return (
    <span
      aria-hidden={decorative}
      className={cn(
        avatarBadgeVariants({ variant, size, position }),
        className
      )}
      role={decorative ? undefined : "status"}
    >
      {!decorative && <span className="sr-only">{label}</span>}
    </span>
  );
}
